// Super Admin → Company Management (master-admin only).
// Self-contained page. Talks ONLY to the new /superadmin/company endpoints —
// nothing in the existing booking/billing/report flow is touched.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  CircularProgress,
  Pagination,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PAGE_SIZE = 10;
const API = (p = '') => `${config.API_BASE_URL}/superadmin/company${p}`;
const token = () => sessionStorage.getItem('token');
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token()}`,
});

const emptyForm = {
  Id: 0,
  Name: '',
  CompanyCode: '',
  Email: '',
  Mobile: '',
  City: '',
  State: '',
  Pincode: '',
  GstNumber: '',
  Currency: '₹',
  PropertType: 'H',
  GstApplicable: true,
  ISActive: true,
  SubscriptionStart: '',
  SubscriptionEnd: '',
};

const toInputDate = (d) => (d ? String(d).slice(0, 10) : '');

const StatCard = ({ label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid #E5E7EB',
      borderLeft: `4px solid ${color}`,
      height: '100%',
    }}>
    <Typography variant='caption' sx={{ color: '#6B7280', fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography variant='h4' sx={{ fontWeight: 800, color: '#111827', mt: 0.5 }}>
      {value ?? 0}
    </Typography>
  </Paper>
);

export default function SuperAdminCompanies() {
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(API('/stats'), { headers: authHeaders() });
      if (r.ok) setStats(await r.json());
    } catch {
      /* non-blocking */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const url = API(
        `?search=${encodeURIComponent(search)}&status=${status}&page=${page}&pageSize=${PAGE_SIZE}`,
      );
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setRows(Array.isArray(d.items) ? d.items : []);
        setTotal(Number(d.total) || 0);
      } else if (r.status === 403) {
        toast.error('Master admin access only.');
        setRows([]);
        setTotal(0);
      }
    } catch {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);
  useEffect(() => {
    loadList();
  }, [loadList]);

  const openAdd = () => {
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (c) => {
    setForm({
      Id: c.id,
      Name: c.name || '',
      CompanyCode: c.companyCode || '',
      Email: c.email || '',
      Mobile: c.mobile || '',
      City: c.city || '',
      State: c.state || '',
      Pincode: c.pincode || '',
      GstNumber: c.gstNumber || '',
      Currency: c.currency || '₹',
      PropertType: c.propertType || 'H',
      GstApplicable: c.gstApplicable !== false,
      ISActive: c.isActive !== false,
      SubscriptionStart: toInputDate(c.subscriptionStart),
      SubscriptionEnd: toInputDate(c.subscriptionEnd),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.Name.trim()) {
      toast.warn('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        SubscriptionStart: form.SubscriptionStart || null,
        SubscriptionEnd: form.SubscriptionEnd || null,
      };
      const isEdit = form.Id > 0;
      const r = await fetch(isEdit ? API(`/${form.Id}`) : API(), {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success(isEdit ? 'Company updated' : 'Company created');
        setOpen(false);
        loadList();
        loadStats();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.message || 'Save failed');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    try {
      const r = await fetch(API(`/${c.id}/status/${!c.isActive}`), {
        method: 'POST',
        headers: authHeaders(),
      });
      if (r.ok) {
        toast.success(c.isActive ? 'Company deactivated' : 'Company activated');
        loadList();
        loadStats();
      } else {
        toast.error('Status update failed');
      }
    } catch {
      toast.error('Status update failed');
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]:
        e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' fontWeight={800} mb={2}>
        🏢 Company Management
      </Typography>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={2.4}>
          <StatCard label='Total Companies' value={stats.total} color='#2563EB' />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard label='Active' value={stats.active} color='#16A34A' />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard label='Inactive' value={stats.inactive} color='#9CA3AF' />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard label='Running' value={stats.running} color='#0EA5E9' />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard label='Expired' value={stats.expired} color='#DC2626' />
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
        <TextField
          size='small'
          label='Search (name / email / mobile / code / city)'
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          sx={{ minWidth: 320, flex: 1 }}
        />
        <TextField
          size='small'
          select
          label='Status'
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          sx={{ minWidth: 160 }}>
          {['all', 'active', 'inactive', 'running', 'expired'].map((s) => (
            <MenuItem key={s} value={s}>
              {s[0].toUpperCase() + s.slice(1)}
            </MenuItem>
          ))}
        </TextField>
        <Button variant='contained' startIcon={<AddIcon />} onClick={openAdd}>
          Add Company
        </Button>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#F9FAFB' }}>
                {[
                  'Company',
                  'Code',
                  'Contact',
                  'City',
                  'Created',
                  'Subscription End',
                  'Status',
                  'Action',
                ].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: '#374151' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align='center' sx={{ py: 5 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align='center' sx={{ py: 5, color: '#6B7280' }}>
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{c.name}</Typography>
                      <Typography variant='caption' color='#6B7280'>
                        {c.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.companyCode || '—'}</TableCell>
                    <TableCell>{c.mobile || '—'}</TableCell>
                    <TableCell>{c.city || '—'}</TableCell>
                    <TableCell>
                      {c.created ? new Date(c.created).toLocaleDateString('en-GB') : '—'}
                    </TableCell>
                    <TableCell>
                      {c.subscriptionEnd
                        ? new Date(c.subscriptionEnd).toLocaleDateString('en-GB')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          size='small'
                          label={c.isActive ? 'Active' : 'Inactive'}
                          color={c.isActive ? 'success' : 'default'}
                          variant={c.isActive ? 'filled' : 'outlined'}
                        />
                        {c.expired && (
                          <Chip size='small' label='Expired' color='error' />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title='Edit'>
                        <IconButton size='small' onClick={() => openEdit(c)}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={c.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size='small'
                          color={c.isActive ? 'error' : 'success'}
                          onClick={() => toggleStatus(c)}>
                          {c.isActive ? (
                            <BlockIcon fontSize='small' />
                          ) : (
                            <CheckCircleIcon fontSize='small' />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant='caption' color='#6B7280'>
            {total} compan{total === 1 ? 'y' : 'ies'}
          </Typography>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, p) => setPage(p)}
            size='small'
            color='primary'
          />
        </Box>
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>{form.Id > 0 ? 'Edit Company' : 'Add Company'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size='small' label='Company Name *' value={form.Name} onChange={set('Name')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size='small' label='Company Code' value={form.CompanyCode} onChange={set('CompanyCode')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='Email' value={form.Email} onChange={set('Email')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='Mobile' value={form.Mobile} onChange={set('Mobile')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='City' value={form.City} onChange={set('City')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='State' value={form.State} onChange={set('State')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='Pincode' value={form.Pincode} onChange={set('Pincode')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size='small' label='GST Number' value={form.GstNumber} onChange={set('GstNumber')} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size='small' label='Currency' value={form.Currency} onChange={set('Currency')} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size='small' select label='Type' value={form.PropertType} onChange={set('PropertType')}>
                <MenuItem value='H'>Hotel</MenuItem>
                <MenuItem value='R'>Restaurant</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size='small'
                type='date'
                label='Subscription Start'
                InputLabelProps={{ shrink: true }}
                value={form.SubscriptionStart}
                onChange={set('SubscriptionStart')}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size='small'
                type='date'
                label='Subscription End'
                InputLabelProps={{ shrink: true }}
                value={form.SubscriptionEnd}
                onChange={set('SubscriptionEnd')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.GstApplicable} onChange={set('GstApplicable')} />}
                label='GST Applicable'
              />
              <FormControlLabel
                control={<Switch checked={form.ISActive} onChange={set('ISActive')} />}
                label='Active'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving || !form.Name.trim()}
            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : null}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
